import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  useToast,
  VStack,
  Divider,
  Alert,
  AlertIcon,
  HStack,
  Text,
  Card,
  CardBody,
  CardHeader,
} from '@chakra-ui/react';
import supabase from '../supabaseClient';

const TherapistSettings = ({ therapistId }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingSignature, setIsUpdatingSignature] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef(null);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const toast = useToast();

  useEffect(() => {
    loadExistingSignature();
    setupCanvas();
  }, [therapistId]);

  const loadExistingSignature = async () => {
    try {
      const { data, error } = await supabase
        .from('therapists')
        .select('signature_image')
        .eq('id', therapistId)
        .single();

      if (error) throw error;
      
      if (data?.signature_image) {
        setSignatureData(data.signature_image);
        // Load signature onto canvas
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = data.signature_image;
      }
    } catch (error) {
      console.error('Error loading signature:', error);
    }
  };

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  };

  const getCanvasPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const pos = getCanvasPos(e);
    setLastPos(pos);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getCanvasPos(e);
    
    ctx.beginPath();
    ctx.moveTo(lastPos.x, lastPos.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setLastPos(pos);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent scrolling when drawing
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = canvasRef.current.width / rect.width;
      const scaleY = canvasRef.current.height / rect.height;
      
      const pos = {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
      
      setIsDrawing(true);
      setLastPos(pos);
    }
  };
  
  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling when drawing
    if (!isDrawing) return;
    
    if (e.touches && e.touches[0]) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = canvasRef.current.width / rect.width;
      const scaleY = canvasRef.current.height / rect.height;
      
      const pos = {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY
      };
      
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      ctx.beginPath();
      ctx.moveTo(lastPos.x, lastPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      
      setLastPos(pos);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const saveSignature = async () => {
    setIsUpdatingSignature(true);
    
    try {
      const canvas = canvasRef.current;
      const signatureDataURL = canvas.toDataURL('image/png');
      
      // Update therapist record with signature
      const { error } = await supabase
        .from('therapists')
        .update({ signature_image: signatureDataURL })
        .eq('id', therapistId);

      if (error) throw error;

      setSignatureData(signatureDataURL);
      
      toast({
        title: 'Signature saved successfully',
        description: 'Your signature will now appear on session notes.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error saving signature',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingSignature(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your new password and confirmation match.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update password using Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      toast({
        title: 'Password Updated',
        description: 'Your password has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Clear the form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast({
        title: 'Password Update Failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box p={4}>
      <Heading size="md" mb={6}>Account Settings</Heading>
      
      <Box maxW="2xl" mx="auto">
        <VStack spacing={8} align="stretch">
          {/* Password Change Section */}
          <Card>
            <CardHeader>
              <Heading size="sm">Change Password</Heading>
            </CardHeader>
            <CardBody>
              <Alert status="info" mb={4}>
                <AlertIcon />
                For security reasons, you can only change your own password.
              </Alert>
              
              <form onSubmit={handlePasswordChange}>
                <VStack spacing={4}>
                  <FormControl id="current-password" isRequired>
                    <FormLabel>Current Password</FormLabel>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </FormControl>
                  
                  <FormControl id="new-password" isRequired>
                    <FormLabel>New Password</FormLabel>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </FormControl>
                  
                  <FormControl id="confirm-password" isRequired>
                    <FormLabel>Confirm New Password</FormLabel>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </FormControl>
                  
                  <Button
                    type="submit"
                    colorScheme="blue"
                    isLoading={isLoading}
                    width="full"
                  >
                    Update Password
                  </Button>
                </VStack>
              </form>
            </CardBody>
          </Card>
          
          {/* Digital Signature Section */}
          <Card>
            <CardHeader>
              <Heading size="sm">Digital Signature</Heading>
            </CardHeader>
            <CardBody>
              <Alert status="info" mb={4}>
                <AlertIcon />
                Create your digital signature to automatically include it in session notes PDFs.
              </Alert>
              
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text mb={2} fontWeight="medium">Draw your signature below:</Text>
                  <Box 
                    borderWidth="2px" 
                    borderColor="gray.300" 
                    borderRadius="md" 
                    p={2}
                    bg="white"
                    cursor="crosshair"
                  >
                  <canvas
                    ref={canvasRef}
                    width={400}
                    height={150}
                    style={{ 
                      width: '100%', 
                      height: '150px',
                      display: 'block',
                      touchAction: 'none' // Add this to prevent scrolling
                    }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={stopDrawing}
                  />
                  </Box>
                </Box>
                
                <HStack spacing={3}>
                  <Button
                    colorScheme="red"
                    variant="outline"
                    onClick={clearSignature}
                    size="sm"
                  >
                    Clear
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={saveSignature}
                    isLoading={isUpdatingSignature}
                    loadingText="Saving..."
                    size="sm"
                  >
                    Save Signature
                  </Button>
                </HStack>
                
                {signatureData && (
                  <Alert status="success">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm">
                        Your signature has been saved and will appear on session note PDFs.
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        You can update it anytime by drawing a new signature and clicking "Save Signature".
                      </Text>
                    </VStack>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </Box>
  );
};

export default TherapistSettings;